import React, { forwardRef, KeyboardEventHandler, useContext, useEffect, useRef } from 'react';
import { IDefaultComponentProps, TModalModeProps } from '@src/models/props';
import { classnames } from '@src/utils/classnames';
import { DialogContext } from '../context';
import { IUnitChildProps } from '@src/core/Unit/models';

import styled from './DialogContainer.module.scss';
import { AnyType } from '@src/models/common';
import { DATA_ELEM_CONTAINER, RIGHT_TRANSITION_CLASSES } from '../const';
import { Animation } from '@src/core/Animation';
import { useOpen } from '@src/hooks/useOpen';

interface IProps extends IDefaultComponentProps, TModalModeProps, IUnitChildProps {
  disableCloseOnEscapeKeyDown?: boolean;
}

export const DialogContainer = forwardRef<HTMLDivElement, IProps>(
  ({ children, className, onVisible, disableCloseOnEscapeKeyDown, mode, ...rest }, ref) => {
    const { isMobile, onClose } = useContext(DialogContext);
    const classes = classnames(styled.root, className, {
      [styled.rootMobile]: isMobile,
      [styled.base]: mode === 'right',
    });

    const prevFocused = useRef<Element | null>(null);
    const openData = useOpen(false);

    const handleKeyUp: KeyboardEventHandler = (e) => {
      if (e.key === 'Escape' && !disableCloseOnEscapeKeyDown) {
        e.stopPropagation();
        onClose?.();
      }
    };

    // Для работы animation нужно изменять состояние open, поэтому по дефолту ставим false
    // и сразу меняем
    // учитываем, что в обратную сторону так не работает и компонент будет размонтирован сверху
    useEffect(() => {
      openData.onOpen();
    }, [openData]);

    /**
     * Так как роддительский элемент контейнера не <Box />,
     * а <div /> поэтому реализовываем эвент показа для юнита
     */
    useEffect(() => {
      onVisible?.();
    }, [onVisible]);

    useEffect(() => {
      prevFocused.current = document.activeElement;
      (ref as AnyType)?.current?.focus();

      return () => {
        if (prevFocused.current) (prevFocused.current as AnyType).focus();
      };
    }, [ref]);

    return (
      <Animation
        open={openData.open}
        transitionClasses={mode === 'right' ? RIGHT_TRANSITION_CLASSES : undefined}
        timeout={mode === 'right' ? 500 : undefined}
      >
        <div tabIndex={0} ref={ref} className={classes} onKeyUp={handleKeyUp} {...rest} data-elem={DATA_ELEM_CONTAINER}>
          {children}
        </div>
      </Animation>
    );
  },
);

DialogContainer.displayName = 'DialogContainer';
